import React from "react";

// http/https only, per SCRUM-59. Matches greedily up to the next whitespace,
// then trailing sentence punctuation is peeled off below so "See
// https://example.com." keeps the sentence's full stop out of the href.
const URL_PATTERN = /https?:\/\/[^\s<>]+/g;

function countChar(text: string, char: string): number {
  let count = 0;
  for (const c of text) if (c === char) count += 1;
  return count;
}

/**
 * Strips trailing characters that almost always belong to the sentence
 * rather than the link: a full stop, comma, or similar closes out a
 * sentence, and a closing bracket/paren only trails the URL if it has no
 * matching opener inside it (so "(see example.com)" loses its paren, but
 * "example.com/wiki/Foo_(bar)" keeps its own).
 */
function stripTrailingPunctuation(url: string): string {
  while (url.length && /[.,;:!?]/.test(url[url.length - 1])) {
    url = url.slice(0, -1);
  }

  let strippedBracket = true;
  while (strippedBracket) {
    strippedBracket = false;
    const last = url[url.length - 1];
    if (last === ")" || last === "]") {
      const open = last === ")" ? "(" : "[";
      const withoutLast = url.slice(0, -1);
      if (countChar(withoutLast, open) <= countChar(withoutLast, last)) {
        url = withoutLast;
        strippedBracket = true;
      }
    }
  }

  return url;
}

/**
 * Splits a plain-text message body into text and detected-link segments.
 *
 * Never renders markup: this only ever produces text nodes and `<a>` tags for
 * substrings that look like URLs. The body itself stays plain text end to
 * end, so there is nothing here for `dangerouslySetInnerHTML` to do.
 */
export function linkifyMessageBody(body: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  URL_PATTERN.lastIndex = 0;
  while ((match = URL_PATTERN.exec(body)) !== null) {
    const url = stripTrailingPunctuation(match[0]);
    const end = match.index + url.length;

    if (!url) continue;

    if (match.index > lastIndex) {
      nodes.push(body.slice(lastIndex, match.index));
    }

    nodes.push(
      <a
        key={`link-${key++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
      >
        {url}
      </a>,
    );

    lastIndex = end;
  }

  if (lastIndex < body.length) {
    nodes.push(body.slice(lastIndex));
  }

  return nodes;
}
